/**
 * Vincent XLSX 2026-07-16 → Eric 2026-07-17 (SPEC STRICK — 520 stabilité perm.)
 *
 * Banner DISCRET affiché en haut de l'app quand axios est en train de retenter
 * automatiquement des requêtes après une erreur 5xx / réseau (Cloudflare 520,
 * pod K8s en redémarrage, hoquet réseau).
 *
 * Objectif : rendre les micro-coupures INVISIBLES pour l'utilisateur.
 * - Pendant un redémarrage K8s de 30-60s, on retry 6 fois (backoff 300ms → 12s)
 * - L'utilisateur voit un petit banner « Reconnexion en cours… » au lieu
 *   d'une erreur alarmiste
 * - Dès qu'une requête réussit, le banner disparaît automatiquement
 * - Si tous les retries échouent : banner rouge « Serveur indisponible »
 */
import { useEffect, useState, useRef } from "react";
import { ArrowsClockwise, WifiHigh, WifiSlash } from "@phosphor-icons/react";

export default function ConnectionRecoveringBanner() {
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState({ attempt: 0, maxRetries: 8, status: null });
  const [severity, setSeverity] = useState("recovering"); // "recovering" | "failed" | "recovered" | "unreachable"
  const [reopenAt, setReopenAt] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const hideTimerRef = useRef(null);

  useEffect(() => {
    const onRecovering = (e) => {
      const d = e.detail || {};
      // B1041 — Si le breaker est déjà ouvert (severity=unreachable), on
      // ignore les events "recovering" — le banner "unreachable" a la
      // priorité (message plus clair, pas de faux compteur 1/8).
      if (severity === "unreachable") return;
      setSeverity("recovering");
      setState({
        attempt: d.attempt || 1,
        maxRetries: d.maxRetries || 8,
        status: d.status || null,
      });
      setVisible(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      // Si aucune nouvelle tentative pendant 3s, on masque
      hideTimerRef.current = setTimeout(() => {
        setSeverity("recovered");
        setTimeout(() => setVisible(false), 2000); // Laisser 2s pour montrer "récupéré"
      }, 3000);
    };
    const onFailed = (e) => {
      const d = e.detail || {};
      setSeverity("failed");
      setState((s) => ({ ...s, status: d.status || null, attempts: d.attempts || 8 }));
      setVisible(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      // Banner reste jusqu'à récupération
    };
    const onServerError = () => {
      // Signal externe qu'une erreur non-retryable est survenue
      setSeverity("failed");
      setVisible(true);
    };
    // B1041 — Circuit-breaker ouvert : le backend est confirmé injoignable
    // (>= 6 échecs réseau en 10s). On affiche un banner distinct avec
    // compte à rebours et on garde la main jusqu'à récupération.
    const onUnreachable = (e) => {
      const d = e.detail || {};
      setSeverity("unreachable");
      setReopenAt(d.reopenAt || (Date.now() + (d.cooldownMs || 30000)));
      setVisible(true);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
    const onRecovered = () => {
      setSeverity("recovered");
      setReopenAt(0);
      setCountdown(0);
      setTimeout(() => setVisible(false), 2000);
    };

    window.addEventListener("knoria:connection-recovering", onRecovering);
    window.addEventListener("knoria:connection-recovery-failed", onFailed);
    window.addEventListener("knoria:server-error", onServerError);
    window.addEventListener("knoria:backend-unreachable", onUnreachable);
    window.addEventListener("knoria:backend-recovered", onRecovered);

    return () => {
      window.removeEventListener("knoria:connection-recovering", onRecovering);
      window.removeEventListener("knoria:connection-recovery-failed", onFailed);
      window.removeEventListener("knoria:server-error", onServerError);
      window.removeEventListener("knoria:backend-unreachable", onUnreachable);
      window.removeEventListener("knoria:backend-recovered", onRecovered);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [severity]);

  // B1041 — Compte à rebours affiché dans le banner "unreachable"
  useEffect(() => {
    if (severity !== "unreachable" || !reopenAt) {
      setCountdown(0);
      return undefined;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((reopenAt - Date.now()) / 1000));
      setCountdown(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [severity, reopenAt]);

  // Vincent B665 (Eric 2026-08-05 SPEC STRICK PROD) — Auto-recovery :
  // quand le banner passe en "failed", on démarre un poll silencieux du
  // health-check (toutes les 5s). Dès que /api/health répond 200, on
  // bascule le banner en "recovered" (visible 2s) puis on le cache — pas
  // besoin de recharger manuellement. Si l'utilisateur clique quand
  // même « Recharger », c'est instantané aussi.
  useEffect(() => {
    if (severity !== "failed" || !visible) return undefined;
    let cancelled = false;
    const API = process.env.REACT_APP_BACKEND_URL;
    const healthCheck = async () => {
      if (cancelled) return;
      try {
        const resp = await fetch(`${API}/api/health`, {
          credentials: "include",
          cache: "no-store",
        });
        if (!cancelled && resp.ok) {
          setSeverity("recovered");
          setTimeout(() => { if (!cancelled) setVisible(false); }, 2000);
          return;
        }
      } catch { /* still down */ }
      if (!cancelled) setTimeout(healthCheck, 5000);
    };
    const t = setTimeout(healthCheck, 5000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [severity, visible]);

  if (!visible) return null;

  const config = {
    recovering: {
      bg: "bg-amber-50 border-amber-300 text-amber-900",
      icon: <ArrowsClockwise size={14} className="animate-spin" />,
      title: "Reconnexion en cours",
      detail: `Tentative ${state.attempt}/${state.maxRetries}${state.status ? ` (HTTP ${state.status})` : ""} — Ta session est intacte.`,
    },
    recovered: {
      bg: "bg-teal-50 border-teal-300 text-teal-900",
      icon: <WifiHigh size={14} weight="bold" />,
      title: "Connexion rétablie",
      detail: "Tout fonctionne à nouveau.",
    },
    failed: {
      bg: "bg-red-50 border-red-300 text-red-900",
      icon: <WifiSlash size={14} weight="bold" />,
      title: "Serveur temporairement indisponible",
      detail: state.status
        ? `Le serveur renvoie HTTP ${state.status}. Ton internet est OK. Redéploiement probablement en cours — patiente 1-2 min puis recharge la page.`
        : "Le serveur ne répond pas. Ton internet est OK. Patiente 1-2 min puis recharge la page.",
    },
    // B1041 — Backend confirmé injoignable (circuit-breaker ouvert).
    unreachable: {
      bg: "bg-orange-50 border-orange-300 text-orange-900",
      icon: <WifiSlash size={14} weight="bold" />,
      title: "Backend Knoria injoignable",
      detail: countdown > 0
        ? `Nouvelle tentative dans ${countdown} s. Ta session est intacte — rien à faire, on ré-essaie tout seul.`
        : "Nouvelle tentative imminente. Ta session est intacte.",
    },
  }[severity];

  return (
    <div
      className={`fixed top-2 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2 rounded-full border shadow-md ${config.bg} flex items-center gap-2 text-xs max-w-[92vw]`}
      data-testid="connection-recovering-banner"
      role="status"
      aria-live="polite"
    >
      {config.icon}
      <span className="font-semibold">{config.title}</span>
      <span className="opacity-80 hidden sm:inline">— {config.detail}</span>
      {severity === "failed" && (
        <button
          onClick={() => window.location.reload()}
          className="ml-2 px-2 py-0.5 rounded bg-white/50 hover:bg-white/80 border border-current/20 font-medium text-[11px]"
          data-testid="connection-recovering-reload-btn"
        >
          Recharger
        </button>
      )}
    </div>
  );
}
